// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { forecastingAPI } from '../services/api';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  CubeIcon,
  TruckIcon,
  BuildingStorefrontIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
  ClockIcon,
  FunnelIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
  ChevronDownIcon,
  TableCellsIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

type CardFilter = 'all' | 'uptrending' | 'downtrending' | 'needsReorder' | 'criticalStock';
type TrendPeriod = 'week' | 'month' | 'year' | '2years';

interface HistoryRecord {
  date: string;
  demand: number;
  forecast: number;
  variance: number;
  notes: string;
}

interface ForecastItem {
  id: string;
  product: string;
  sku: string;
  category: string;
  currentDemand: number;
  forecastedDemand: number;
  trend: 'up' | 'down' | 'stable';
  trendPercentage: number;
  confidence: number;
  seasonality: 'high' | 'medium' | 'low';
  stockStatus: 'adequate' | 'low' | 'critical' | 'excess';
  recommendedOrder: number;
  leadTime: number;
  supplier: string;
}

interface DemandPeriod {
  month: string;
  actual: number;
  forecast: number;
  lowerBound: number;
  upperBound: number;
}

const mockForecastData: ForecastItem[] = [
  {
    id: 'FC-001',
    product: 'Industrial Lubricant 5L',
    sku: 'SKU-78542',
    category: 'Maintenance Supplies',
    currentDemand: 1250,
    forecastedDemand: 1420,
    trend: 'up',
    trendPercentage: 13.6,
    confidence: 92,
    seasonality: 'medium',
    stockStatus: 'low',
    recommendedOrder: 500,
    leadTime: 7,
    supplier: 'Midwest Manufacturing Co.',
  },
  {
    id: 'FC-002',
    product: 'Steel Fasteners Kit',
    sku: 'SKU-45231',
    category: 'Hardware',
    currentDemand: 3200,
    forecastedDemand: 2850,
    trend: 'down',
    trendPercentage: -10.9,
    confidence: 88,
    seasonality: 'low',
    stockStatus: 'adequate',
    recommendedOrder: 0,
    leadTime: 5,
    supplier: 'Texas Parts Supply',
  },
  {
    id: 'FC-003',
    product: 'Electronic Control Module',
    sku: 'SKU-99012',
    category: 'Electronics',
    currentDemand: 480,
    forecastedDemand: 720,
    trend: 'up',
    trendPercentage: 50.0,
    confidence: 78,
    seasonality: 'high',
    stockStatus: 'critical',
    recommendedOrder: 350,
    leadTime: 14,
    supplier: 'Northwest Electronics',
  },
  {
    id: 'FC-004',
    product: 'Hydraulic Pump Assembly',
    sku: 'SKU-33789',
    category: 'Heavy Equipment',
    currentDemand: 85,
    forecastedDemand: 92,
    trend: 'up',
    trendPercentage: 8.2,
    confidence: 95,
    seasonality: 'low',
    stockStatus: 'adequate',
    recommendedOrder: 0,
    leadTime: 21,
    supplier: 'Pacific Imports Ltd.',
  },
  {
    id: 'FC-005',
    product: 'Safety Gloves (Box 100)',
    sku: 'SKU-12456',
    category: 'Safety Equipment',
    currentDemand: 890,
    forecastedDemand: 1100,
    trend: 'up',
    trendPercentage: 23.6,
    confidence: 85,
    seasonality: 'medium',
    stockStatus: 'low',
    recommendedOrder: 400,
    leadTime: 3,
    supplier: 'Southern Foods Inc.',
  },
  {
    id: 'FC-006',
    product: 'Diesel Fuel Additive 1L',
    sku: 'SKU-67890',
    category: 'Fleet Supplies',
    currentDemand: 2100,
    forecastedDemand: 1950,
    trend: 'down',
    trendPercentage: -7.1,
    confidence: 91,
    seasonality: 'high',
    stockStatus: 'excess',
    recommendedOrder: 0,
    leadTime: 5,
    supplier: 'Central Midwest Supply',
  },
  {
    id: 'FC-007',
    product: 'Warehouse Pallet Rack',
    sku: 'SKU-55123',
    category: 'Storage Equipment',
    currentDemand: 45,
    forecastedDemand: 48,
    trend: 'stable',
    trendPercentage: 6.7,
    confidence: 82,
    seasonality: 'low',
    stockStatus: 'adequate',
    recommendedOrder: 0,
    leadTime: 28,
    supplier: 'Atlantic Cargo Services',
  },
  {
    id: 'FC-008',
    product: 'Packaging Tape (Case 36)',
    sku: 'SKU-88901',
    category: 'Packaging',
    currentDemand: 1560,
    forecastedDemand: 1680,
    trend: 'up',
    trendPercentage: 7.7,
    confidence: 89,
    seasonality: 'medium',
    stockStatus: 'adequate',
    recommendedOrder: 200,
    leadTime: 2,
    supplier: 'East Coast Distributors',
  },
];

// Weekly demand data (last 8 weeks)
const weeklyDemandData: DemandPeriod[] = [
  { month: 'Week 1', actual: 11200, forecast: 11000, lowerBound: 10500, upperBound: 11500 },
  { month: 'Week 2', actual: 12100, forecast: 11800, lowerBound: 11200, upperBound: 12400 },
  { month: 'Week 3', actual: 11800, forecast: 12200, lowerBound: 11600, upperBound: 12800 },
  { month: 'Week 4', actual: 13200, forecast: 12500, lowerBound: 11900, upperBound: 13100 },
  { month: 'Week 5', actual: 12900, forecast: 13000, lowerBound: 12400, upperBound: 13600 },
  { month: 'Week 6', actual: 0, forecast: 13200, lowerBound: 12600, upperBound: 13800 },
  { month: 'Week 7', actual: 0, forecast: 13500, lowerBound: 12800, upperBound: 14200 },
  { month: 'Week 8', actual: 0, forecast: 13800, lowerBound: 13100, upperBound: 14500 },
];

// Monthly demand data (last 12 months)
const monthlyDemandData: DemandPeriod[] = [
  { month: 'Jul 2023', actual: 45200, forecast: 44000, lowerBound: 42000, upperBound: 46000 },
  { month: 'Aug 2023', actual: 48500, forecast: 47200, lowerBound: 45000, upperBound: 49400 },
  { month: 'Sep 2023', actual: 52100, forecast: 51000, lowerBound: 48500, upperBound: 53500 },
  { month: 'Oct 2023', actual: 49800, forecast: 50500, lowerBound: 48000, upperBound: 53000 },
  { month: 'Nov 2023', actual: 55200, forecast: 54000, lowerBound: 51500, upperBound: 56500 },
  { month: 'Dec 2023', actual: 62500, forecast: 60000, lowerBound: 57000, upperBound: 63000 },
  { month: 'Jan 2024', actual: 58100, forecast: 57500, lowerBound: 54500, upperBound: 60500 },
  { month: 'Feb 2024', actual: 0, forecast: 54000, lowerBound: 51000, upperBound: 57000 },
  { month: 'Mar 2024', actual: 0, forecast: 56500, lowerBound: 53000, upperBound: 60000 },
  { month: 'Apr 2024', actual: 0, forecast: 59000, lowerBound: 55500, upperBound: 62500 },
  { month: 'May 2024', actual: 0, forecast: 61500, lowerBound: 57500, upperBound: 65500 },
  { month: 'Jun 2024', actual: 0, forecast: 64000, lowerBound: 59500, upperBound: 68500 },
];

// Yearly demand data (last 4 years by quarter)
const yearlyDemandData: DemandPeriod[] = [
  { month: 'Q1 2023', actual: 145000, forecast: 142000, lowerBound: 135000, upperBound: 149000 },
  { month: 'Q2 2023', actual: 158000, forecast: 155000, lowerBound: 147000, upperBound: 163000 },
  { month: 'Q3 2023', actual: 162000, forecast: 160000, lowerBound: 152000, upperBound: 168000 },
  { month: 'Q4 2023', actual: 178000, forecast: 172000, lowerBound: 163000, upperBound: 181000 },
  { month: 'Q1 2024', actual: 165000, forecast: 168000, lowerBound: 159000, upperBound: 177000 },
  { month: 'Q2 2024', actual: 0, forecast: 175000, lowerBound: 166000, upperBound: 184000 },
  { month: 'Q3 2024', actual: 0, forecast: 182000, lowerBound: 173000, upperBound: 191000 },
  { month: 'Q4 2024', actual: 0, forecast: 195000, lowerBound: 185000, upperBound: 205000 },
];

// 2-year demand data (by half-year)
const twoYearDemandData: DemandPeriod[] = [
  { month: 'H1 2022', actual: 285000, forecast: 280000, lowerBound: 266000, upperBound: 294000 },
  { month: 'H2 2022', actual: 312000, forecast: 305000, lowerBound: 290000, upperBound: 320000 },
  { month: 'H1 2023', actual: 303000, forecast: 310000, lowerBound: 294000, upperBound: 326000 },
  { month: 'H2 2023', actual: 340000, forecast: 332000, lowerBound: 315000, upperBound: 349000 },
  { month: 'H1 2024', actual: 165000, forecast: 343000, lowerBound: 326000, upperBound: 360000 },
  { month: 'H2 2024', actual: 0, forecast: 377000, lowerBound: 358000, upperBound: 396000 },
];

// Get demand data based on selected period
const getDemandDataByPeriod = (period: TrendPeriod): DemandPeriod[] => {
  switch (period) {
    case 'week':
      return weeklyDemandData;
    case 'month':
      return monthlyDemandData;
    case 'year':
      return yearlyDemandData;
    case '2years':
      return twoYearDemandData;
    default:
      return monthlyDemandData;
  }
};

// Generate mock history records for a forecast item
const generateHistoryRecords = (item: ForecastItem): HistoryRecord[] => {
  const records: HistoryRecord[] = [];
  const baseDate = new Date();
  const baseDemand = item.currentDemand;

  for (let i = 11; i >= 0; i--) {
    const date = new Date(baseDate);
    date.setMonth(date.getMonth() - i);
    const variance = Math.floor((Math.random() - 0.5) * 20);
    const demand = Math.floor(baseDemand * (0.8 + Math.random() * 0.4));
    const forecast = Math.floor(demand * (1 + variance / 100));

    const notes = i === 0 ? 'Current period' :
                  variance > 10 ? 'Above forecast - seasonal spike' :
                  variance < -10 ? 'Below forecast - supply chain delay' :
                  'Within expected range';

    records.push({
      date: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      demand,
      forecast,
      variance,
      notes,
    });
  }

  return records;
};

// Get unique suppliers for filter dropdown
const getUniqueSuppliers = () => [...new Set(mockForecastData.map(item => item.supplier))];

const Forecasting: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const cameFromAlerts = useRef(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('3months');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

  // Card filter state
  const [cardFilter, setCardFilter] = useState<CardFilter>('all');

  // Detail modal state
  const [selectedForecast, setSelectedForecast] = useState<ForecastItem | null>(null);

  // Handle URL query params for deep linking from Alerts (product)
  useEffect(() => {
    const productId = searchParams.get('product');
    if (productId) {
      // Find product by SKU or ID
      const forecast = mockForecastData.find(f =>
        f.sku === productId ||
        f.id === productId ||
        f.sku.toLowerCase().includes(productId.toLowerCase()) ||
        productId.toLowerCase().includes(f.sku.toLowerCase())
      );
      if (forecast) {
        cameFromAlerts.current = true;
        setSelectedForecast(forecast);
      }
      // Clear the query param after handling
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Handle closing forecast detail modal - return to Alerts if came from there
  const handleCloseForecastDetail = () => {
    setSelectedForecast(null);
    if (cameFromAlerts.current) {
      cameFromAlerts.current = false;
      navigate('/alerts');
    }
  };

  // More Filters state
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState<string>('');
  const [stockStatusFilter, setStockStatusFilter] = useState<string>('');
  const [minConfidence, setMinConfidence] = useState<number>(0);
  const [seasonalityFilter, setSeasonalityFilter] = useState<string>('');

  // Demand Trend period state
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>('month');

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyForecast, setHistoryForecast] = useState<ForecastItem | null>(null);

  // Purchase Order modal state
  const [showPOModal, setShowPOModal] = useState(false);
  const [poForecast, setPOForecast] = useState<ForecastItem | null>(null);
  const [poQuantity, setPOQuantity] = useState<number>(0);

  // AI Insights state
  const [aiInsights, setAiInsights] = useState<string[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Export dropdown state
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Apply card-based filtering
  const applyCardFilter = (item: ForecastItem): boolean => {
    switch (cardFilter) {
      case 'uptrending':
        return item.trend === 'up';
      case 'downtrending':
        return item.trend === 'down';
      case 'needsReorder':
        return item.recommendedOrder > 0;
      case 'criticalStock':
        return item.stockStatus === 'critical';
      case 'all':
      default:
        return true;
    }
  };

  const filteredForecasts = useMemo(() => {
    return mockForecastData.filter((item) => {
      const matchesCategory = !selectedCategory || item.category === selectedCategory;
      const matchesCardFilter = applyCardFilter(item);
      const matchesSupplier = !supplierFilter || item.supplier === supplierFilter;
      const matchesStockStatus = !stockStatusFilter || item.stockStatus === stockStatusFilter;
      const matchesConfidence = item.confidence >= minConfidence;
      const matchesSeasonality = !seasonalityFilter || item.seasonality === seasonalityFilter;
      return matchesCategory && matchesCardFilter && matchesSupplier && matchesStockStatus && matchesConfidence && matchesSeasonality;
    });
  }, [selectedCategory, cardFilter, supplierFilter, stockStatusFilter, minConfidence, seasonalityFilter]);

  const hasActiveMoreFilters = supplierFilter || stockStatusFilter || minConfidence > 0 || seasonalityFilter;

  // Fetch AI insights when filtered forecasts change
  const fetchAIInsights = useCallback(async (forecasts: ForecastItem[]) => {
    if (forecasts.length === 0) {
      setAiInsights([]);
      return;
    }

    setInsightsLoading(true);
    setInsightsError(null);

    try {
      const response = await forecastingAPI.aiInsights(forecasts);
      setAiInsights(response.data.insights || []);
    } catch (error) {
      console.error('Error fetching AI insights:', error);
      setInsightsError('Unable to generate AI insights. Please try again later.');
      // Set fallback insights based on the data
      const fallbackInsights: string[] = [];
      const critical = forecasts.filter(f => f.stockStatus === 'critical');
      const uptrending = forecasts.filter(f => f.trend === 'up');
      const needsReorder = forecasts.filter(f => f.recommendedOrder > 0);

      if (critical.length > 0) {
        fallbackInsights.push(`**Critical Stock Alert:** ${critical.map(f => f.product).join(', ')} require immediate attention to prevent stockouts.`);
      }
      if (uptrending.length > 0) {
        const topUp = uptrending.sort((a, b) => b.trendPercentage - a.trendPercentage)[0];
        fallbackInsights.push(`**Demand Surge:** ${topUp.product} shows ${topUp.trendPercentage}% demand increase - consider increasing safety stock.`);
      }
      if (needsReorder.length > 0) {
        fallbackInsights.push(`**Reorder Required:** ${needsReorder.length} products need purchase orders placed based on current forecasts.`);
      }
      if (fallbackInsights.length === 0) {
        fallbackInsights.push('All inventory levels appear stable. Continue monitoring demand trends.');
      }
      setAiInsights(fallbackInsights);
    } finally {
      setInsightsLoading(false);
    }
  }, []);

  // Trigger AI insights fetch when filtered forecasts change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchAIInsights(filteredForecasts);
    }, 500); // Debounce to prevent too many API calls

    return () => clearTimeout(debounceTimer);
  }, [filteredForecasts, fetchAIInsights]);

  // Refresh forecast data and AI insights
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    // Simulate data refresh delay (in production, this would fetch from API)
    await new Promise(resolve => setTimeout(resolve, 800));
    // Force re-fetch AI insights
    await fetchAIInsights(filteredForecasts);
    setIsRefreshing(false);
  }, [filteredForecasts, fetchAIInsights]);

  // Export filtered forecast data to CSV
  const handleExportCSV = useCallback(() => {
    const headers = [
      'Product',
      'SKU',
      'Category',
      'Current Demand',
      'Forecasted Demand',
      'Trend',
      'Trend %',
      'Confidence %',
      'Seasonality',
      'Stock Status',
      'Recommended Order',
      'Lead Time (days)',
      'Supplier'
    ];

    const rows = filteredForecasts.map(item => [
      item.product,
      item.sku,
      item.category,
      item.currentDemand,
      item.forecastedDemand,
      item.trend,
      item.trendPercentage,
      item.confidence,
      item.seasonality,
      item.stockStatus,
      item.recommendedOrder,
      item.leadTime,
      item.supplier
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell =>
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `forecast_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
  }, [filteredForecasts]);

  // Export filtered forecast data to Word (DOCX-compatible HTML)
  const handleExportWord = useCallback(() => {
    const dateStr = new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <title>Demand Forecast Report</title>
        <style>
          body { font-family: Calibri, Arial, sans-serif; padding: 20px; }
          h1 { color: #0d9488; border-bottom: 2px solid #0d9488; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 30px; }
          table { border-collapse: collapse; width: 100%; margin-top: 20px; }
          th { background-color: #0d9488; color: white; padding: 12px 8px; text-align: left; font-weight: bold; }
          td { border: 1px solid #d1d5db; padding: 10px 8px; }
          tr:nth-child(even) { background-color: #f3f4f6; }
          .summary { background-color: #f0fdfa; border: 1px solid #14b8a6; padding: 15px; margin: 20px 0; border-radius: 4px; }
          .critical { color: #dc2626; font-weight: bold; }
          .warning { color: #d97706; }
          .success { color: #059669; }
          .trend-up { color: #059669; }
          .trend-down { color: #dc2626; }
        </style>
      </head>
      <body>
        <h1>Demand Forecast Report</h1>
        <p><strong>Generated:</strong> ${dateStr}</p>
        <p><strong>Total Products:</strong> ${filteredForecasts.length}</p>

        <div class="summary">
          <h3 style="margin-top: 0;">Summary</h3>
          <p><strong>Uptrending Products:</strong> ${filteredForecasts.filter(f => f.trend === 'up').length}</p>
          <p><strong>Downtrending Products:</strong> ${filteredForecasts.filter(f => f.trend === 'down').length}</p>
          <p><strong>Products Needing Reorder:</strong> ${filteredForecasts.filter(f => f.recommendedOrder > 0).length}</p>
          <p><strong>Critical Stock Items:</strong> ${filteredForecasts.filter(f => f.stockStatus === 'critical').length}</p>
        </div>

        <h2>Forecast Details</h2>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Category</th>
              <th>Current</th>
              <th>Forecast</th>
              <th>Trend</th>
              <th>Confidence</th>
              <th>Stock Status</th>
              <th>Recommended Order</th>
              <th>Supplier</th>
            </tr>
          </thead>
          <tbody>
            ${filteredForecasts.map(item => `
              <tr>
                <td>${item.product}</td>
                <td>${item.sku}</td>
                <td>${item.category}</td>
                <td>${item.currentDemand.toLocaleString()}</td>
                <td>${item.forecastedDemand.toLocaleString()}</td>
                <td class="${item.trend === 'up' ? 'trend-up' : item.trend === 'down' ? 'trend-down' : ''}">${item.trend === 'up' ? '+' : ''}${item.trendPercentage}%</td>
                <td>${item.confidence}%</td>
                <td class="${item.stockStatus === 'critical' ? 'critical' : item.stockStatus === 'low' ? 'warning' : 'success'}">${item.stockStatus}</td>
                <td>${item.recommendedOrder > 0 ? item.recommendedOrder.toLocaleString() + ' units' : '-'}</td>
                <td>${item.supplier}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        ${aiInsights.length > 0 ? `
          <h2>AI Insights</h2>
          <ul>
            ${aiInsights.map(insight => `<li>${insight}</li>`).join('')}
          </ul>
        ` : ''}
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `forecast_report_${new Date().toISOString().split('T')[0]}.doc`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
  }, [filteredForecasts, aiInsights]);

  // Export filtered forecast data to PDF (via print dialog)
  const handleExportPDF = useCallback(() => {
    const dateStr = new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Demand Forecast Report</title>
        <style>
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 1100px; margin: 0 auto; }
          h1 { color: #0d9488; border-bottom: 3px solid #0d9488; padding-bottom: 15px; margin-bottom: 20px; }
          h2 { color: #374151; margin-top: 30px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
          table { border-collapse: collapse; width: 100%; margin-top: 15px; font-size: 11px; }
          th { background-color: #0d9488; color: white; padding: 10px 6px; text-align: left; font-weight: 600; }
          td { border: 1px solid #d1d5db; padding: 8px 6px; }
          tr:nth-child(even) { background-color: #f9fafb; }
          .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 20px 0; }
          .summary-card { background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%); border: 1px solid #14b8a6; padding: 15px; border-radius: 8px; text-align: center; }
          .summary-card .number { font-size: 28px; font-weight: bold; color: #0d9488; }
          .summary-card .label { font-size: 12px; color: #6b7280; margin-top: 5px; }
          .critical { color: #dc2626; font-weight: 600; }
          .warning { color: #d97706; }
          .success { color: #059669; }
          .trend-up { color: #059669; font-weight: 500; }
          .trend-down { color: #dc2626; font-weight: 500; }
          .insights { background-color: #f0fdfa; border-left: 4px solid #14b8a6; padding: 15px 20px; margin: 20px 0; }
          .insights ul { margin: 10px 0; padding-left: 20px; }
          .insights li { margin: 8px 0; line-height: 1.5; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; text-align: center; }
        </style>
      </head>
      <body>
        <h1>Demand Forecast Report</h1>
        <p><strong>Generated:</strong> ${dateStr} | <strong>Products:</strong> ${filteredForecasts.length}</p>

        <div class="summary-grid">
          <div class="summary-card">
            <div class="number">${filteredForecasts.filter(f => f.trend === 'up').length}</div>
            <div class="label">Uptrending</div>
          </div>
          <div class="summary-card">
            <div class="number">${filteredForecasts.filter(f => f.trend === 'down').length}</div>
            <div class="label">Downtrending</div>
          </div>
          <div class="summary-card">
            <div class="number">${filteredForecasts.filter(f => f.recommendedOrder > 0).length}</div>
            <div class="label">Need Reorder</div>
          </div>
          <div class="summary-card">
            <div class="number" style="color: ${filteredForecasts.filter(f => f.stockStatus === 'critical').length > 0 ? '#dc2626' : '#0d9488'}">${filteredForecasts.filter(f => f.stockStatus === 'critical').length}</div>
            <div class="label">Critical Stock</div>
          </div>
        </div>

        ${aiInsights.length > 0 ? `
          <div class="insights">
            <h3 style="margin-top: 0; color: #0d9488;">AI Insights</h3>
            <ul>
              ${aiInsights.map(insight => `<li>${insight.replace(/\*\*/g, '')}</li>`).join('')}
            </ul>
          </div>
        ` : ''}

        <h2>Forecast Details</h2>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Category</th>
              <th>Current</th>
              <th>Forecast</th>
              <th>Trend</th>
              <th>Conf.</th>
              <th>Status</th>
              <th>Order Qty</th>
              <th>Supplier</th>
            </tr>
          </thead>
          <tbody>
            ${filteredForecasts.map(item => `
              <tr>
                <td>${item.product}</td>
                <td>${item.sku}</td>
                <td>${item.category}</td>
                <td>${item.currentDemand.toLocaleString()}</td>
                <td>${item.forecastedDemand.toLocaleString()}</td>
                <td class="${item.trend === 'up' ? 'trend-up' : item.trend === 'down' ? 'trend-down' : ''}">${item.trend === 'up' ? '+' : ''}${item.trendPercentage}%</td>
                <td>${item.confidence}%</td>
                <td class="${item.stockStatus === 'critical' ? 'critical' : item.stockStatus === 'low' ? 'warning' : 'success'}">${item.stockStatus}</td>
                <td>${item.recommendedOrder > 0 ? item.recommendedOrder.toLocaleString() : '-'}</td>
                <td>${item.supplier}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          Auxilio Logistics - Demand Forecast Report | Generated on ${dateStr}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      // Slight delay to ensure content is rendered
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
    setShowExportMenu(false);
  }, [filteredForecasts, aiInsights]);

  // Get demand data based on selected trend period
  const currentDemandData = useMemo(() => getDemandDataByPeriod(trendPeriod), [trendPeriod]);

  const categories = [...new Set(mockForecastData.map((item) => item.category))];

  const getTrendIcon = (trend: ForecastItem['trend'], percentage: number) => {
    if (trend === 'up') {
      return <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />;
    } else if (trend === 'down') {
      return <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />;
    }
    return <span className="w-4 h-4 text-gray-400">—</span>;
  };

  const getStockStatusBadge = (status: ForecastItem['stockStatus']) => {
    const styles = {
      adequate: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      low: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      excess: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    };
    const labels = {
      adequate: 'Adequate',
      low: 'Low Stock',
      critical: 'Critical',
      excess: 'Excess',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600 dark:text-green-400';
    if (confidence >= 80) return 'text-blue-600 dark:text-blue-400';
    if (confidence >= 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const stats = {
    totalProducts: mockForecastData.length,
    uptrending: mockForecastData.filter((f) => f.trend === 'up').length,
    downtrending: mockForecastData.filter((f) => f.trend === 'down').length,
    needsReorder: mockForecastData.filter((f) => f.recommendedOrder > 0).length,
    criticalStock: mockForecastData.filter((f) => f.stockStatus === 'critical').length,
    avgConfidence: Math.round(mockForecastData.reduce((acc, f) => acc + f.confidence, 0) / mockForecastData.length),
  };

  const maxDemand = Math.max(...currentDemandData.map((d) => d.upperBound));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Demand Forecasting</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            AI-powered demand predictions and inventory recommendations
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Forecast'}
          </button>
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Export Report
              <ChevronDownIcon className="w-4 h-4 ml-2" />
            </button>
            {showExportMenu && (
              <>
                {/* Backdrop to close menu when clicking outside */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowExportMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                  <div className="py-1">
                    <button
                      onClick={handleExportCSV}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <TableCellsIcon className="w-4 h-4 mr-3 text-green-600" />
                      Export as CSV
                    </button>
                    <button
                      onClick={handleExportWord}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <DocumentTextIcon className="w-4 h-4 mr-3 text-blue-600" />
                      Export as Word
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <DocumentArrowDownIcon className="w-4 h-4 mr-3 text-red-600" />
                      Export as PDF
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <button
          onClick={() => setCardFilter('all')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'all'
              ? 'border-teal-500 ring-2 ring-teal-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Products</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalProducts}</p>
            </div>
            <CubeIcon className="w-8 h-8 text-teal-500" />
          </div>
        </button>
        <button
          onClick={() => setCardFilter('uptrending')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'uptrending'
              ? 'border-green-500 ring-2 ring-green-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Uptrending</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.uptrending}</p>
            </div>
            <ArrowTrendingUpIcon className="w-8 h-8 text-green-500" />
          </div>
        </button>
        <button
          onClick={() => setCardFilter('downtrending')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'downtrending'
              ? 'border-red-500 ring-2 ring-red-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Downtrending</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.downtrending}</p>
            </div>
            <ArrowTrendingDownIcon className="w-8 h-8 text-red-500" />
          </div>
        </button>
        <button
          onClick={() => setCardFilter('needsReorder')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'needsReorder'
              ? 'border-yellow-500 ring-2 ring-yellow-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Need Reorder</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.needsReorder}</p>
            </div>
            <TruckIcon className="w-8 h-8 text-yellow-500" />
          </div>
        </button>
        <button
          onClick={() => setCardFilter('criticalStock')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'criticalStock'
              ? 'border-red-500 ring-2 ring-red-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Critical Stock</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.criticalStock}</p>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
          </div>
        </button>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg Confidence</p>
              <p className={`text-2xl font-bold ${getConfidenceColor(stats.avgConfidence)}`}>{stats.avgConfidence}%</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Demand Trend Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Demand Trend</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Historical vs forecasted demand (units)</p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Period Selector */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              {[
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' },
                { value: 'year', label: 'Year' },
                { value: '2years', label: '2 Years' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTrendPeriod(option.value as TrendPeriod)}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    trendPeriod === option.value
                      ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-teal-500 rounded"></span>
                <span className="text-gray-600 dark:text-gray-300">Actual</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-blue-500 rounded"></span>
                <span className="text-gray-600 dark:text-gray-300">Forecast</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="w-3 h-0.5 bg-gray-300 dark:bg-gray-600"></span>
                <span className="text-gray-600 dark:text-gray-300">Range</span>
              </div>
            </div>
          </div>
        </div>

        {/* Simple bar chart visualization */}
        <div className="relative">
          {/* Chart bars area */}
          <div className="h-48 flex items-end justify-between gap-1">
            {currentDemandData.map((period, index) => (
              <div key={period.month} className="flex-1 flex flex-col items-center h-full">
                <div className="w-full flex flex-col items-center relative h-full">
                  {/* Confidence range */}
                  <div
                    className="absolute w-full bg-gray-100 dark:bg-gray-700 rounded opacity-50"
                    style={{
                      height: `${((period.upperBound - period.lowerBound) / maxDemand) * 100}%`,
                      bottom: `${(period.lowerBound / maxDemand) * 100}%`,
                    }}
                  ></div>
                  {/* Actual bar */}
                  {period.actual > 0 && (
                    <div
                      className="absolute w-3 bg-teal-500 rounded-t"
                      style={{
                        height: `${(period.actual / maxDemand) * 100}%`,
                        bottom: 0,
                        left: 'calc(50% - 10px)',
                      }}
                    ></div>
                  )}
                  {/* Forecast bar */}
                  <div
                    className={`absolute w-3 rounded-t ${period.actual > 0 ? 'bg-blue-500' : 'bg-blue-400'}`}
                    style={{
                      height: `${(period.forecast / maxDemand) * 100}%`,
                      bottom: 0,
                      left: 'calc(50% + 2px)',
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          {/* X-axis labels - positioned below the chart */}
          <div className="flex justify-between gap-1 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            {currentDemandData.map((period) => (
              <div key={period.month} className="flex-1 flex justify-center">
                <span className="text-xs text-gray-500 dark:text-gray-400 transform -rotate-45 origin-top whitespace-nowrap">
                  {period.month}
                </span>
              </div>
            ))}
          </div>
          {/* Extra spacing for rotated labels */}
          <div className="h-8"></div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
            >
              <option value="1month">1 Month</option>
              <option value="3months">3 Months</option>
              <option value="6months">6 Months</option>
              <option value="12months">12 Months</option>
            </select>
            <button
              onClick={() => setShowMoreFilters(!showMoreFilters)}
              className={`inline-flex items-center px-4 py-2 border rounded-lg transition-colors ${
                showMoreFilters || hasActiveMoreFilters
                  ? 'border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-500'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              More Filters
              {hasActiveMoreFilters && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-teal-600 rounded-full">
                  {[supplierFilter, stockStatusFilter, minConfidence > 0, seasonalityFilter].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
          {/* View Mode Toggle */}
          <div className="flex gap-1 border border-gray-300 dark:border-gray-600 rounded-lg p-1 bg-white dark:bg-gray-700">
            <div className="relative group">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded transition-colors ${viewMode === 'table' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                aria-label="Table View"
              >
                <TableCellsIcon className="w-4 h-4" />
              </button>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                Table View
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
              </div>
            </div>
            <div className="relative group">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded transition-colors ${viewMode === 'cards' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                aria-label="Card View"
              >
                <Squares2X2Icon className="w-4 h-4" />
              </button>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                Card View
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
              </div>
            </div>
          </div>
        </div>

        {/* More Filters Panel */}
        {showMoreFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Supplier
                </label>
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Suppliers</option>
                  {getUniqueSuppliers().map((supplier) => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Stock Status
                </label>
                <select
                  value={stockStatusFilter}
                  onChange={(e) => setStockStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Status</option>
                  <option value="adequate">Adequate</option>
                  <option value="low">Low Stock</option>
                  <option value="critical">Critical</option>
                  <option value="excess">Excess</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Min Confidence: {minConfidence}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={minConfidence}
                  onChange={(e) => setMinConfidence(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Seasonality
                </label>
                <select
                  value={seasonalityFilter}
                  onChange={(e) => setSeasonalityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Seasonality</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-between items-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredForecasts.length} of {mockForecastData.length} products
              </p>
              <button
                onClick={() => {
                  setSupplierFilter('');
                  setStockStatusFilter('');
                  setMinConfidence(0);
                  setSeasonalityFilter('');
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Forecast Table/Cards */}
      {viewMode === 'table' ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Current Demand
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Forecast
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Trend
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Confidence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Stock Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Recommended Order
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredForecasts.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors"
                    onClick={() => setSelectedForecast(item)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                          <CubeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{item.product}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{item.sku}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{item.currentDemand.toLocaleString()} units</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{item.category}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {item.forecastedDemand.toLocaleString()} units
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {getTrendIcon(item.trend, item.trendPercentage)}
                        <span className={`text-sm font-medium ${
                          item.trend === 'up' ? 'text-green-600 dark:text-green-400' :
                          item.trend === 'down' ? 'text-red-600 dark:text-red-400' :
                          'text-gray-600 dark:text-gray-400'
                        }`}>
                          {item.trendPercentage > 0 ? '+' : ''}{item.trendPercentage}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              item.confidence >= 90 ? 'bg-green-500' :
                              item.confidence >= 80 ? 'bg-blue-500' :
                              item.confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${item.confidence}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-medium ${getConfidenceColor(item.confidence)}`}>
                          {item.confidence}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStockStatusBadge(item.stockStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.recommendedOrder > 0 ? (
                        <div>
                          <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">
                            {item.recommendedOrder.toLocaleString()} units
                          </span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Lead time: {item.leadTime} days
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">No action needed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredForecasts.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 cursor-pointer hover:shadow-md hover:border-teal-300 dark:hover:border-teal-600 transition-all"
              onClick={() => setSelectedForecast(item)}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{item.product}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{item.sku}</p>
                </div>
                {getStockStatusBadge(item.stockStatus)}
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Current Demand</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {item.currentDemand.toLocaleString()} units
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Forecasted</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {item.forecastedDemand.toLocaleString()} units
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Trend</span>
                  <div className="flex items-center gap-1">
                    {getTrendIcon(item.trend, item.trendPercentage)}
                    <span className={`text-sm font-medium ${
                      item.trend === 'up' ? 'text-green-600' : item.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {item.trendPercentage > 0 ? '+' : ''}{item.trendPercentage}%
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Confidence</span>
                  <span className={`text-sm font-medium ${getConfidenceColor(item.confidence)}`}>
                    {item.confidence}%
                  </span>
                </div>
              </div>

              {item.recommendedOrder > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Recommended Order</p>
                      <p className="text-lg font-bold text-teal-600 dark:text-teal-400">
                        {item.recommendedOrder.toLocaleString()} units
                      </p>
                    </div>
                    <button className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700">
                      Create PO
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Insights Panel */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/20 rounded-lg">
            {insightsLoading ? (
              <ArrowPathIcon className="w-6 h-6 animate-spin" />
            ) : (
              <InformationCircleIcon className="w-6 h-6" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">AI Insights</h3>
              {insightsLoading && (
                <span className="text-sm text-teal-100">Analyzing {filteredForecasts.length} products...</span>
              )}
            </div>
            {insightsLoading ? (
              <div className="space-y-3">
                <div className="h-4 bg-white/20 rounded animate-pulse w-full"></div>
                <div className="h-4 bg-white/20 rounded animate-pulse w-3/4"></div>
                <div className="h-4 bg-white/20 rounded animate-pulse w-5/6"></div>
              </div>
            ) : insightsError ? (
              <div className="flex items-start gap-2 text-sm text-teal-50">
                <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{insightsError}</span>
              </div>
            ) : aiInsights.length > 0 ? (
              <ul className="space-y-2 text-sm text-teal-50">
                {aiInsights.map((insight, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="prose prose-sm prose-invert max-w-none [&>p]:m-0 [&>strong]:text-white [&>em]:text-teal-100">
                      <ReactMarkdown>{insight}</ReactMarkdown>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-teal-50">No insights available for the current selection.</p>
            )}
          </div>
        </div>
      </div>

      {/* Forecast Detail Modal */}
      {selectedForecast && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <CubeIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedForecast.product}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{selectedForecast.sku} • {selectedForecast.category}</p>
                </div>
              </div>
              <button
                onClick={handleCloseForecastDetail}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Status Row */}
              <div className="flex items-center gap-3">
                {getStockStatusBadge(selectedForecast.stockStatus)}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  selectedForecast.seasonality === 'high'
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                    : selectedForecast.seasonality === 'medium'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {selectedForecast.seasonality.charAt(0).toUpperCase() + selectedForecast.seasonality.slice(1)} Seasonality
                </span>
              </div>

              {/* Demand Comparison */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Current Demand</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedForecast.currentDemand.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">units/period</p>
                </div>
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4">
                  <p className="text-sm text-teal-600 dark:text-teal-400 mb-1">Forecasted Demand</p>
                  <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">
                    {selectedForecast.forecastedDemand.toLocaleString()}
                  </p>
                  <p className="text-xs text-teal-600 dark:text-teal-400">units/period</p>
                </div>
              </div>

              {/* Trend & Confidence */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Demand Trend</p>
                  <div className="flex items-center gap-2">
                    {selectedForecast.trend === 'up' ? (
                      <ArrowTrendingUpIcon className="w-8 h-8 text-green-500" />
                    ) : selectedForecast.trend === 'down' ? (
                      <ArrowTrendingDownIcon className="w-8 h-8 text-red-500" />
                    ) : (
                      <span className="w-8 h-8 flex items-center justify-center text-2xl text-gray-400">—</span>
                    )}
                    <span className={`text-2xl font-bold ${
                      selectedForecast.trend === 'up' ? 'text-green-600 dark:text-green-400' :
                      selectedForecast.trend === 'down' ? 'text-red-600 dark:text-red-400' :
                      'text-gray-600 dark:text-gray-400'
                    }`}>
                      {selectedForecast.trendPercentage > 0 ? '+' : ''}{selectedForecast.trendPercentage}%
                    </span>
                  </div>
                </div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Forecast Confidence</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            selectedForecast.confidence >= 90 ? 'bg-green-500' :
                            selectedForecast.confidence >= 80 ? 'bg-blue-500' :
                            selectedForecast.confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${selectedForecast.confidence}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className={`text-2xl font-bold ${getConfidenceColor(selectedForecast.confidence)}`}>
                      {selectedForecast.confidence}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Supply Chain Info */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Supply Chain Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <BuildingStorefrontIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Supplier</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedForecast.supplier}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ClockIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Lead Time</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedForecast.leadTime} days</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommended Action */}
              {selectedForecast.recommendedOrder > 0 ? (
                <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-teal-800 dark:text-teal-300">Recommended Order</p>
                      <p className="text-3xl font-bold text-teal-700 dark:text-teal-400">
                        {selectedForecast.recommendedOrder.toLocaleString()} units
                      </p>
                      <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                        Order by {new Date(Date.now() + (selectedForecast.leadTime * 24 * 60 * 60 * 1000)).toLocaleDateString()} to meet forecasted demand
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setPOForecast(selectedForecast);
                        setPOQuantity(selectedForecast.recommendedOrder);
                        setShowPOModal(true);
                      }}
                      className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Create Purchase Order
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="text-sm font-semibold text-green-800 dark:text-green-300">No Action Required</p>
                      <p className="text-xs text-green-600 dark:text-green-400">Current stock levels are sufficient to meet forecasted demand</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCloseForecastDetail}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setHistoryForecast(selectedForecast);
                  setShowHistoryModal(true);
                }}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
              >
                View Full History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && historyForecast && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <DocumentTextIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Demand History</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{historyForecast.product} • {historyForecast.sku}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setHistoryForecast(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Avg Monthly Demand</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {Math.floor(historyForecast.currentDemand * 0.95).toLocaleString()}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Forecast Accuracy</p>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">94.2%</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Peak Month</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">December</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">YoY Growth</p>
                  <p className={`text-xl font-bold ${historyForecast.trend === 'up' ? 'text-green-600 dark:text-green-400' : historyForecast.trend === 'down' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {historyForecast.trend === 'up' ? '+' : ''}{historyForecast.trendPercentage}%
                  </p>
                </div>
              </div>

              {/* History Table */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Period</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actual Demand</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Forecasted</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Variance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {generateHistoryRecords(historyForecast).map((record, index) => (
                      <tr key={index} className={index === 0 ? 'bg-teal-50 dark:bg-teal-900/20' : ''}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{record.date}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{record.demand.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{record.forecast.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`font-medium ${
                            record.variance > 0 ? 'text-green-600 dark:text-green-400' :
                            record.variance < 0 ? 'text-red-600 dark:text-red-400' :
                            'text-gray-600 dark:text-gray-400'
                          }`}>
                            {record.variance > 0 ? '+' : ''}{record.variance}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{record.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Info Note */}
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-medium">Historical Analysis</p>
                    <p className="mt-1 text-blue-600 dark:text-blue-400">
                      This product shows {historyForecast.seasonality} seasonality patterns.
                      {historyForecast.seasonality === 'high' && ' Expect significant fluctuations during peak seasons.'}
                      {historyForecast.seasonality === 'medium' && ' Moderate demand variations observed throughout the year.'}
                      {historyForecast.seasonality === 'low' && ' Demand remains relatively stable across seasons.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setHistoryForecast(null);
                }}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Order Modal */}
      {showPOModal && poForecast && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <ShoppingCartIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create Purchase Order</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Draft PO for {poForecast.product}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowPOModal(false);
                  setPOForecast(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* PO Number */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Draft PO Number</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">PO-{Date.now().toString().slice(-8)}</p>
                  </div>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full text-sm font-medium">
                    Draft
                  </span>
                </div>
              </div>

              {/* Product Info */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Product Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Product</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{poForecast.product}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">SKU</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{poForecast.sku}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Category</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{poForecast.category}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Supplier</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{poForecast.supplier}</p>
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Order Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={poQuantity}
                      onChange={(e) => setPOQuantity(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Recommended: {poForecast.recommendedOrder.toLocaleString()} units
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Lead Time</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{poForecast.leadTime} days</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Expected Delivery</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {new Date(Date.now() + (poForecast.leadTime * 24 * 60 * 60 * 1000)).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Integration Notice */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-medium">ERP Integration</p>
                    <p className="mt-1 text-blue-600 dark:text-blue-400">
                      In a production environment, this would create a purchase order in your ERP system
                      (SAP, Oracle, NetSuite, etc.) and automatically route it through your approval workflow.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowPOModal(false);
                  setPOForecast(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // In production, this would submit to ERP
                  alert(`Purchase Order Draft Created!\n\nPO Number: PO-${Date.now().toString().slice(-8)}\nProduct: ${poForecast.product}\nQuantity: ${poQuantity.toLocaleString()} units\nSupplier: ${poForecast.supplier}\n\nThis is a demo - in production, this would integrate with your ERP system.`);
                  setShowPOModal(false);
                  setPOForecast(null);
                  handleCloseForecastDetail();
                }}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
              >
                Create Draft PO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forecasting;
